/**
 * 本番環境の内部ドメイン（外資就活）のホスト名
 */
const PRODUCTION_INTERNAL_HOST = 'gaishishukatsu.com';

/**
 * 環境変数から現在の環境のホスト名を取得する
 * process.envから直接読み取ることで、実行時の環境変数を反映する
 * @returns 現在の環境のホスト名（取得できない場合はnull）
 */
const getCurrentEnvHost = (): string | null => {
  const domainUrl = process.env.NEXT_PUBLIC_DOMAIN_URL;
  if (!domainUrl) {
    return null;
  }
  try {
    const url = new URL(domainUrl);
    return url.hostname.toLowerCase();
  } catch {
    return null;
  }
};

/**
 * 判定結果の型
 */
type ExternalEntryUrlResult = {
  /** entry_urlまたはlink_urlパラメータが存在し、かつ外部サイトのURLである場合true */
  isExternal: boolean;
  /** 見つかったパラメータ名（entry_url または link_url） */
  paramName: 'entry_url' | 'link_url' | null;
  /** パラメータの値（URL） */
  paramValue: string | null;
};

/**
 * 許可するプロトコル（http/httpsのみ）
 */
const ALLOWED_PROTOCOLS = ['http:', 'https:'];

/**
 * URLが内部ドメインかどうかを判定する
 * @param hostname ホスト名
 * @returns 内部ドメインの場合true
 */
const isInternalHost = (hostname: string): boolean => {
  // 完全一致またはサブドメインのみを内部サイトとして扱う
  // （fakegaishishukatsu.comのようなドメインを防ぐため）
  return (
    hostname === PRODUCTION_INTERNAL_HOST ||
    hostname.endsWith(`.${PRODUCTION_INTERNAL_HOST}`)
  );
};

/**
 * URLが外部サイトかどうかを判定する
 * 現在の環境のドメインと本番ドメイン（gaishishukatsu.com）を内部サイトとして扱う
 * @param url 判定対象のURL
 * @returns 外部サイトの場合true
 */
const isExternalUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);

    // http/https以外のプロトコル（javascript:, data:, file:など）は
    // セキュリティリスクがあるため、内部サイト（isExternal=false）として扱う
    if (!ALLOWED_PROTOCOLS.includes(parsedUrl.protocol)) {
      return false;
    }

    const hostname = parsedUrl.hostname.toLowerCase();

    // 本番ドメイン（gaishishukatsu.com）またはそのサブドメインは内部サイト
    if (isInternalHost(hostname)) {
      return false;
    }

    // 現在の環境のホスト名と一致する場合も内部サイト
    const currentEnvHost = getCurrentEnvHost();
    if (currentEnvHost && hostname === currentEnvHost) {
      return false;
    }

    return true;
  } catch {
    // URLのパースに失敗した場合（相対パスなど）は内部サイトとみなす
    return false;
  }
};

/**
 * entryUrlがクエリパラメータとしてentry_urlまたはlink_urlを持ち、
 * かつそのURLが外部サイトである場合を判定する
 *
 * @param entryUrl エントリーボタンのURL
 * @returns 判定結果
 *
 * @example
 * ```ts
 * const result = checkExternalEntryUrl('https://example.com?entry_url=https://external.com/apply');
 * // result.isExternal === true
 * // result.paramName === 'entry_url'
 * // result.paramValue === 'https://external.com/apply'
 *
 * const result2 = checkExternalEntryUrl('https://example.com?entry_url=https://gaishishukatsu.com/apply');
 * // result2.isExternal === false
 * // result2.paramName === 'entry_url'
 * // result2.paramValue === 'https://gaishishukatsu.com/apply'
 *
 * const result3 = checkExternalEntryUrl('https://example.com');
 * // result3.isExternal === false
 * // result3.paramName === null
 * // result3.paramValue === null
 * ```
 */
export const checkExternalEntryUrl = (
  entryUrl: string,
): ExternalEntryUrlResult => {
  const defaultResult: ExternalEntryUrlResult = {
    isExternal: false,
    paramName: null,
    paramValue: null,
  };

  try {
    const url = new URL(entryUrl);

    // ベースURL自体のプロトコルチェック
    // javascript:, data:, file:などの危険なプロトコルは拒否
    if (!ALLOWED_PROTOCOLS.includes(url.protocol)) {
      return defaultResult;
    }

    const searchParams = url.searchParams;

    // entry_urlまたはlink_urlパラメータを探す
    const entryUrlParam = searchParams.get('entry_url');
    const linkUrlParam = searchParams.get('link_url');

    if (entryUrlParam) {
      return {
        isExternal: isExternalUrl(entryUrlParam),
        paramName: 'entry_url',
        paramValue: entryUrlParam,
      };
    }

    if (linkUrlParam) {
      return {
        isExternal: isExternalUrl(linkUrlParam),
        paramName: 'link_url',
        paramValue: linkUrlParam,
      };
    }

    return defaultResult;
  } catch {
    // URLのパースに失敗した場合
    return defaultResult;
  }
};
